import React, { useEffect } from 'react';
import { Toast } from 'react-bootstrap';
import { LoadingSpinner } from '../ai2/components/LoadingSpinner';

interface LoadingContextType {
  isLoading: boolean;
  loadingPreferences: boolean;
  startLoading: (message?: string) => void;
  stopLoading: () => void;
  setLoadingPreferences: React.Dispatch<React.SetStateAction<boolean>>;
}
const LoadingContext = React.createContext<LoadingContextType>({
  isLoading: false,
  loadingPreferences: false,
  startLoading: () => {},
  stopLoading: () => {},
  setLoadingPreferences: () => {},
});

export const useGlobalLoading = () => React.useContext(LoadingContext);

const LoadingProvider = ({ children }: { children: React.ReactNode }) => {
  const [message, setMessage] = React.useState<string>('Loading data');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [loadingPreferences, setLoadingPreferences] =
    React.useState<boolean>(false);
  const stopLoading = () => setIsLoading(false);
  const startLoading = (message = 'Loading data') => {
    setMessage(message);
    setIsLoading(true);
  };
  const value = React.useMemo(
    () => ({
      setLoadingPreferences,
      loadingPreferences,
      isLoading,
      stopLoading,
      startLoading,
    }),
    [isLoading, loadingPreferences],
  );

  useEffect(() => {
    if (!isLoading && !loadingPreferences) {
      stopLoading();
    }
  }, [isLoading, loadingPreferences]);

  return (
    <LoadingContext.Provider value={value}>
      <Toast
        show={isLoading || loadingPreferences}
        onClose={stopLoading}
        className="loading-overlay"
      >
        <Toast.Body className="d-flex align-items-center">
          <div style={{ transform: 'scale(0.6)', transformOrigin: 'center' }}>
            <LoadingSpinner showText="" />
          </div>
          <span className="loading-text">
            {message} <span className="dots" />
          </span>
        </Toast.Body>
      </Toast>
      {children}
    </LoadingContext.Provider>
  );
};

export default LoadingProvider;
